/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import ExecutionEnvironment from 'exenv';
import { breakpoints } from '@tcp/core/styles/themes/TCP/mediaQuery';
import NextSeo from '@tcp/web/src/components/common/atoms/SEO';
import Row from '@tcp/core/src/components/common/atoms/Row';
import Col from '@tcp/core/src/components/common/atoms/Col';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Image from '@tcp/core/src/components/common/atoms/Image';
import Constants from '@tcp/core/src/components/common/molecules/Recommendations/container/Recommendations.constants';
import { isTier1Device } from '@tcp/web/src/utils/device-tiering';
import BundleProductsGrid from '@tcp/core/src/components/features/browse/BundleProduct/molecules/BundleProductsGrid/views/BundleProductsGrid.view.jsx';
import {
  getViewportInfo,
  getIconPath,
  routerPush,
  getSessionStorage,
  getSiteId,
  getVideoUrl,
} from '@tcp/core/src/utils';
import {
  TOGGLE_OPTION_CONFIG,
  NEW_DESIGN_STORAGE_KEY,
} from '@tcp/core/src/components/features/browse/OutfitDetails/config';
import ToastModal from '@tcp/core/src/components/features/account/common/molecule/ToastModal/views/ToastModal.jsx';
import BundleProductListLoadingComponent from '@tcp/core/src/components/features/browse/BundleProduct/molecules/BundleProductListView/views/BundleProductListViewSkeleton.view';
import BundleProductListLoaderContext from '@tcp/core/src/components/features/browse/BundleProduct/molecules/BundleProductListView/views/BundleProductListContext';
import BuildYourExperienceEntryBlock from '@tcp/core/src/components/features/browse/BundleProduct/atoms/BuildYourExperienceEntryBlock';
import {
  getLabelFromCopyOrFromTranslation,
  getDynamicLabelValues,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import FullSizeImageModal from '@tcp/core/src/components/features/browse/ProductDetail/molecules/FullSizeImageModal/views/FullSizeImageModal.view';
import { PDP_SUB_LABELS, pdpSubLabels } from '@tcp/core/src/utils/labels/browse/PDP.labels';
import Carousel from '../../../../common/molecules/Carousel';
import withStyles from '../../../../common/hoc/withStyles';
import styles from '../styles/BundleProduct.style';
import {
  StyledPriceContainer,
  StyledToggleOptionWrapper,
} from '../styles/BundleProductNewDesign.style';
import getPrefixId from '../../ProductDetail/molecules/ProductDescription/views/ProductDescription.view.util';
import FixedBreadCrumbs from '../../ProductListing/molecules/FixedBreadCrumbs/views';
import SocialConnect from '../../../../common/organisms/ProductImages/views/SocialConnect.view';
import ProductDetailImage from '../../../../common/molecules/ProductDetailImage';
import {
  CAROUSEL_OPTIONS_NEW_DESIGN,
  IMG_DATA,
  COMBINED_KITS_CONFIG,
  COMBINED_KITS_CONFIG_UNMOUNT,
} from './config';
import {
  getImagesToDisplay,
  getMapSliceForColorProductId,
  filterBundleImageUrl,
  getMapSliceForColor,
  getPricesWithRange,
  getProductListToPath,
} from '../../ProductListing/molecules/ProductList/utils/productsCommonUtils';
import PromoPDPBanners from '../../../../common/organisms/PromoPDPBanners';
import BundleProductSkeletonNewDesign from '../molecules/BundleProductSkeletonNewDesign';
import Anchor from '../../../../common/atoms/Anchor/views/Anchor';
import { getImageUrl, getRoutePath, getSeoProps } from './Bundleutils';
import LoaderSkelton from '../../../../common/molecules/LoaderSkelton';
import {
  StyledPaginatedGridSkeleton,
  StyledLoaderSkeleton,
} from '../molecules/BundleProductSkeletonNewDesign/styles/BundleProductSkeletonNewDesign.style';

const Recommendations = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "recommendations" */ '@tcp/core/src/components/common/molecules/Recommendations'
  )
);

const Switch = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "switch" */ '@tcp/core/src/components/features/browse/OutfitDetails/molecules/Switch'
    ),
  {
    loading: () => <StyledLoaderSkeleton className="switch-skeleton" />,
  }
);

const BundleProductListView = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "bundle-product-list-view" */ '@tcp/core/src/components/features/browse/BundleProduct/molecules/BundleProductListView'
    ),
  {
    loading: () => <BundleProductListLoadingComponent />,
  }
);

const BuildYourExperienceDepartmentView = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "build-your-experience-department-view" */ '@tcp/core/src/components/features/browse/BundleProduct/molecules/BuildYourExperienceDepartmentView'
  )
);

const iconConfig = {
  twitter: 'twitter-grey',
  pinterest: 'pinterest-grey',
  facebook: 'facebook-grey',
};

const shouldDisplayPJPlannerEntryBlock = ({
  isPJPlannerExperienceEnabled,
  displayBuildYourCollectionExperience,
  hasCombinedKits,
  departmentList,
}) => {
  return (
    isPJPlannerExperienceEnabled &&
    !displayBuildYourCollectionExperience &&
    departmentList.length > 0 &&
    hasCombinedKits
  );
};

const shouldDisplayFullWidthPJPlannerEntryBlock = ({
  isPJPlannerExperienceEnabled,
  displayBuildYourCollectionExperience,
  hasCombinedKits,
  departmentList,
}) => {
  return (
    isPJPlannerExperienceEnabled &&
    !displayBuildYourCollectionExperience &&
    departmentList.length > 0 &&
    !hasCombinedKits
  );
};

const getBuildYourExperienceClass = (displayBuildYourCollectionExperience) => {
  return displayBuildYourCollectionExperience ? ' build-your-experience-pj-planner-container' : '';
};

const getRemoveMarginTopClass = (hasCombinedKits) => {
  return hasCombinedKits ? '' : ' remove-margin-top';
};

const getBuildYourExperienceImageClass = (displayBuildYourCollectionExperience) => {
  return displayBuildYourCollectionExperience ? ' build-your-experience-main-bundle-image' : '';
};

const getCollectionZoomClass = (isCollectionsZoomEnabled) => {
  return isCollectionsZoomEnabled ? ' collection-zoom-image' : '';
};

class BundleProductNewDesign extends React.PureComponent {
  constructor(props) {
    super(props);
    this.carouselRef = React.createRef();
    const storedDefaultView = getSessionStorage(NEW_DESIGN_STORAGE_KEY);
    this.state = {
      slidesToShowDynamic: 1,
      imagesToDisplay: [],
      imageFlag: false,
      containerWidth: 'auto',
      currentView: storedDefaultView || props?.defaultView || TOGGLE_OPTION_CONFIG[0].label,
      hasMounted: false,
      displayBuildYourCollectionExperience: false,
      isFullSizeModalOpen: false,
      currentImageIndex: 0,
    };
    this.selectedPDPImage = '';
    this.handleShowHideFullSizeModalClick = this.handleShowHideFullSizeModalClick.bind(this);
  }

  static getDerivedStateFromProps(props) {
    const { currentProduct, isOnModelAbTestBundlePdp } = props;

    if (currentProduct) {
      let imagesToDisplay = [];
      const { colorFitsSizesMap, generalProductId } = currentProduct;

      if (colorFitsSizesMap) {
        imagesToDisplay = getImagesToDisplay({
          imagesByColor: currentProduct.imagesByColor,
          curentColorEntry: getMapSliceForColorProductId(colorFitsSizesMap, generalProductId),
          isAbTestActive: isOnModelAbTestBundlePdp,
          isFullSet: true,
        });
      }
      return {
        imagesToDisplay,
      };
    }
    return null;
  }

  dynamicBrowseLabels = getDynamicLabelValues('dynamic.browse_d');

  pdpSubLabelsObj = getLabelFromCopyOrFromTranslation(PDP_SUB_LABELS, pdpSubLabels);

  componentDidMount() {
    const { isCombinedKitsEnabled } = this.props;
    if (isCombinedKitsEnabled) {
      this.updateContainerWidth();
    }
    this.setState({ hasMounted: true });
    const urlParams = new URLSearchParams(window.location.search);
    const isPJPView = urlParams.get('isPlannerView');

    if (isPJPView === 'true') {
      this.setState({
        displayBuildYourCollectionExperience: true,
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { isCombinedKitsEnabled, kitGrouping } = this.props;
    if (!isEqual(prevProps?.kitGrouping, kitGrouping) && isCombinedKitsEnabled) {
      this.updateContainerWidth();
    }
  }

  updateContainerWidth = () => {
    const isDesktop = getViewportInfo()?.isDesktop;
    const { kitGrouping } = this.props;
    const parentElements = document?.getElementsByClassName('main-bundle-description');
    if (!this.carouselRef.current) {
      return;
    }
    if (this.carouselRef.current) {
      const container = isDesktop ? parentElements[0] : this.carouselRef.current;
      const slideWidth = 97; // Adjust this to the actual width of your slides
      const totalSlides = kitGrouping?.length;
      const containerDynamicWidth = slideWidth * totalSlides;
      const isContainerFit = containerDynamicWidth < container.offsetWidth;
      const finalContainerWidth = isContainerFit
        ? `${containerDynamicWidth}px`
        : `${container.offsetWidth}px`;
      let slidesToShow = isContainerFit
        ? containerDynamicWidth / slideWidth
        : container.offsetWidth / slideWidth;
      if (isDesktop) {
        slidesToShow = Math.floor(slidesToShow);
      }
      // Update the container width and slidesToShowDynamic state
      this.setState({
        containerWidth: finalContainerWidth,
        slidesToShowDynamic: slidesToShow > 0 ? slidesToShow : 1,
      });
    }
  };

  getBreadCrumb = () => {
    const { breadCrumbs } = this.props;
    if (breadCrumbs) {
      return (
        <Row className="breadcrumb-container">
          <Col colSize={{ small: 6, medium: 8, large: 12 }} className="breadcrum-wrapper">
            <FixedBreadCrumbs crumbs={breadCrumbs} separationChar=">" />
          </Col>
        </Row>
      );
    }
    return '';
  };

  getSocialConnectWidget = () => {
    const { isBundlePageRedesign } = this.props;

    return (
      <SocialConnect
        className="bundle-social-wrapper-redesign"
        isFacebookEnabled
        isPinterestEnabled
        isTwitterEnabled
        iconConfig={iconConfig}
        isBundlePageRedesign={isBundlePageRedesign}
      />
    );
  };

  getColorname = (colorProduct) => colorProduct.color && colorProduct.color.name;

  getProductSummary = (currentColorEntry) => {
    const {
      currentProduct,
      currencyAttributes,
      isOutfitPage,
      errorMessages,
      isBundlePageRedesign,
      isHideOriginalPrice,
    } = this.props;
    const { displayBuildYourCollectionExperience } = this.state;

    const selectedColorProductId = currentColorEntry && currentColorEntry.colorProductId;

    const colorProduct =
      getMapSliceForColorProductId(currentProduct.colorFitsSizesMap, selectedColorProductId) || {};
    const colorProductName = this.getColorname(colorProduct);

    const prices = getPricesWithRange(currentProduct, colorProductName);
    return (
      <Row fullBleed>
        <Col
          colSize={{ small: 6, medium: 8, large: 12 }}
          className={`product-price-col${displayBuildYourCollectionExperience ? ' hide-mpr' : ''}`}
        >
          <div className="product-summary-wrapper-new-design">
            <h1 className="title-wrapper-new-design">{currentProduct.name}</h1>
            {this.getSocialConnectWidget()}
          </div>
          {!displayBuildYourCollectionExperience && (
            <StyledPriceContainer
              currencyAttributes={currencyAttributes}
              isItemPartNumberVisible={false}
              itemPartNumber={colorProduct.colorDisplayId}
              {...prices}
              isOutfitPage={isOutfitPage}
              errorMessages={errorMessages}
              isHideOriginalPrice={isHideOriginalPrice}
              isBundlePageRedesign={isBundlePageRedesign}
            />
          )}
        </Col>
      </Row>
    );
  };

  getProductDescription = (currentColorEntry) => {
    const ClaimMessage = renderLabel(
      this.pdpSubLabelsObj.lbl_product_description_claim_message,
      'lbl_product_description_claim_message'
    );

    const PartNumber = renderLabel(
      this.pdpSubLabelsObj.lbl_product_description_item_part_number,
      'lbl_product_description_item_part_number'
    );
    const aboutThisCollection = renderLabel(
      this.pdpSubLabelsObj.lbl_about_this_collection,
      'lbl_about_this_collection'
    );
    const { shortDescription, longDescription, currentProduct } = this.props;
    const itemColor = currentColorEntry && currentColorEntry.color && currentColorEntry.color.name;
    const { colorFitsSizesMap, getMultiPackAllColor } = currentProduct;
    const colorSlice = getMapSliceForColor(colorFitsSizesMap, itemColor);
    const colorDisplayId = colorSlice && colorSlice.colorDisplayId;
    const prefixId = getPrefixId(getMultiPackAllColor, colorDisplayId, PartNumber);
    const productItemId = `${prefixId} ${colorDisplayId}`;
    return (
      <Row fullBleed>
        <Col colSize={{ small: 6, medium: 8, large: 12 }} className="product-description">
          <div className="product-desc">
            <div className="product-desc-title">
              <span className="product-desc-title-text">{aboutThisCollection}</span>
              <span className="product-desc-title-partno">{productItemId}</span>
            </div>
            <div className="product-desc-body">
              {shortDescription}
              <BodyCopy
                className="list-content"
                component="ul"
                dangerouslySetInnerHTML={{
                  __html: longDescription.replace(/<li>/g, '<li class="list-child">'),
                }}
                fontSize="fs14"
                fontFamily="secondary"
              />
              <BodyCopy
                className="list-content"
                component="span"
                dangerouslySetInnerHTML={{
                  __html: ClaimMessage,
                }}
                fontSize="fs14"
                fontFamily="secondary"
              />
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  // This is required for reommendations.
  getCatIdForRecommendation = () => {
    const { breadCrumbs } = this.props;
    if (breadCrumbs) {
      const category = breadCrumbs.map((crumb, index) => {
        const { displayName } = crumb;
        const separationChar = index !== breadCrumbs.length - 1 ? ':' : '';
        return displayName + separationChar;
      });
      return category.join('');
    }
    return '';
  };

  getRecommendations = () => {
    const youMayAlsoLike = renderLabel(
      this.dynamicBrowseLabels.lbl_you_may_also_like_collection,
      'lbl_you_may_also_like_collection'
    );

    const recentlyViewed = renderLabel(
      this.dynamicBrowseLabels.lbl_recently_viewed_collection,
      'lbl_recently_viewed_collection'
    );

    const { itemPartNumber, currentProduct } = this.props;
    const { primaryCategory } = currentProduct;
    const willMonetateCarouselDisplay = isTier1Device();
    const categoryId = this.getCatIdForRecommendation();
    const recommendationAttributes = {
      variations: 'moduleO',
      page: Constants.RECOMMENDATIONS_PAGES_MAPPING.COLLECTION,
      categoryName: categoryId,
      partNumber: itemPartNumber,
      showLoyaltyPromotionMessage: false,
      headerAlignment: 'left',
      priceOnly: true,
      recommendationCarouselRedesign: true,
      primaryCategory: primaryCategory || '',
    };
    return (
      <div className="product-detail-section">
        <Recommendations headerLabel={youMayAlsoLike} {...recommendationAttributes} sequence="1" />
        {willMonetateCarouselDisplay && (
          <Recommendations
            headerLabel={recentlyViewed}
            portalValue={Constants.RECOMMENDATIONS_MBOXNAMES.RECENTLY_VIEWED}
            {...recommendationAttributes}
            sequence="2"
          />
        )}
      </div>
    );
  };

  onImageLoad = (imageFlag) => {
    if (!imageFlag) {
      this.setState({ imageFlag: true });
    }
  };

  renderCollectionInfo = (collectionName) => (
    <div>
      <span className="product-desc-title-text" data-locator="product-desc-title-text">
        {'Collection: '}
      </span>
      <BodyCopy
        className="list-content"
        component="span"
        dangerouslySetInnerHTML={{
          __html: collectionName,
        }}
        fontSize="fs14"
        fontFamily="secondary"
        data-locator="combined-kits-list-content"
      />
    </div>
  );

  renderCarouselItem = (item, index) => {
    const { carryForwardImgUrlActn, isFromBundlePage, currentProduct, clearBundleDetails } =
      this.props;
    const productId = currentProduct?.productId;
    const asPath = getRoutePath(item, item.brand);
    const toPath = `/${getSiteId()}${asPath}`;
    const updatedToPath = getProductListToPath(asPath);
    const active = item.productid === productId;
    const imgCls = `related-bundle-carousal-image ${active ? 'active' : ''}`;
    const anchorCls = `related-bundle-carousal-anchor ${active ? 'active' : ''}`;

    const handleLinkClick = active
      ? (event) => event.preventDefault()
      : (event) => {
          event.preventDefault();
          carryForwardImgUrlActn(getImageUrl(item));
          this.setState({ imageFlag: false }, () => {
            clearBundleDetails();
            routerPush(updatedToPath, asPath);
          });
        };

    return (
      <Anchor
        to={toPath}
        asPath={asPath}
        noLink
        handleLinkClick={handleLinkClick}
        data-locator={`combined-kit-product-anchor-${index}`}
        className={anchorCls}
      >
        <ProductDetailImage
          className={imgCls}
          imageUrl={getImageUrl(item)}
          imageName={item.seo_token}
          isZoomEnabled={false}
          imageConfig={IMG_DATA.imgConfig}
          primaryBrand={item.brand}
          isFromBundlePage={isFromBundlePage}
          data-locator={`combined-kit-product-detail-image-${index}`}
          noLink
          onOpenSimpleFullSize={(e) => e.preventDefault()}
        />
      </Anchor>
    );
  };

  getHasCombinedKits = ({ isCombinedKitsEnabled, kitGrouping }) => {
    return isCombinedKitsEnabled && kitGrouping?.length > 0;
  };

  combinedKits = (isMobile) => {
    const { currentProduct, isCombinedKitsEnabled, kitGrouping } = this.props;
    const { slidesToShowDynamic, containerWidth, hasMounted } = this.state;
    COMBINED_KITS_CONFIG.slidesToShow = slidesToShowDynamic;
    const collectionName = currentProduct?.name?.split('-')[1];
    const hasCombinedKits = this.getHasCombinedKits({ isCombinedKitsEnabled, kitGrouping });
    if (hasCombinedKits) {
      return (
        <>
          {kitGrouping.length > 8 && !isMobile && (
            <div
              className="combined-kits-wrapper combined-kits  hide-on-tablet hide-on-mobile"
              ref={this.carouselRef}
              data-locator="combined-kits-wrapper"
            >
              {this.renderCollectionInfo(collectionName)}
              <div
                className={`combined-kits-caraosal ${!hasMounted ? 'carousel-desktop-width' : ''}`}
                style={{ width: containerWidth }}
                data-locator="combined-kits-caraosal"
              >
                <Carousel
                  options={hasMounted ? COMBINED_KITS_CONFIG : COMBINED_KITS_CONFIG_UNMOUNT}
                  carouselConfig={{
                    autoplay: false,
                    customArrowLeft: getIconPath('icon-carrot-grey-large'),
                    customArrowRight: getIconPath('icon-carrot-grey-large'),
                  }}
                  data-locator="combined-kit-carousel"
                  mountedSetting={true}
                >
                  {kitGrouping?.map((item, index) => this.renderCarouselItem(item, index))}
                </Carousel>
              </div>
            </div>
          )}
          <div
            className={`combined-kits-wrapper ${kitGrouping.length > 8 ? 'hide-on-desktop' : ''}`}
            ref={this.carouselRef}
            data-locator="combined-kits-wrapper"
          >
            {this.renderCollectionInfo(collectionName)}
            <div
              className="combined-kits-caraosal scroll-container-combined-kits"
              data-locator="combined-kits-caraosal"
            >
              {kitGrouping?.map((item, index) => this.renderCarouselItem(item, index))}
            </div>
          </div>
        </>
      );
    }
    return (
      isCombinedKitsEnabled &&
      !currentProduct && (
        <div className="combined-kit-skeleton">
          <LoaderSkelton />
        </div>
      )
    );
  };

  renderPlpPdpMainImage = () => {
    const {
      currentProduct,
      isOutfitPage,
      alternateBrand,
      plpImagesToDisplay,
      isFromBundlePage,
      isCombinedKitsEnabled,
      carryForwardImgUrlValue,
      isCollectionsZoomEnabled,
      isPinchZoomPDPImageEnabled,
      retrievedProductInfo,
    } = this.props;
    const { imageConfigWithoutDPR = '' } = retrievedProductInfo || {};
    const { imageFlag, imagesToDisplay, isFullSizeModalOpen } = this.state;
    const primaryBrand = currentProduct?.primaryBrand || alternateBrand;
    const imageUrl = plpImagesToDisplay?.[0]?.regularSizeImageUrl || carryForwardImgUrlValue;
    const isDesktop =
      ExecutionEnvironment.canUseDOM && document.body.offsetWidth >= breakpoints.values.lg;

    if (plpImagesToDisplay?.length <= 0 && !(isCombinedKitsEnabled && carryForwardImgUrlValue)) {
      return this.getMainImageCarousel(imagesToDisplay, imageFlag);
    }
    if (!imageFlag) {
      return (
        <>
          <ProductDetailImage
            className="outfit-carousal-image"
            imageUrl={imageUrl}
            imageName={currentProduct.name}
            isZoomEnabled={false}
            imageConfig={IMG_DATA.imgConfig}
            isOutfitPage={isOutfitPage}
            primaryBrand={primaryBrand}
            fromPLPPage={true}
            isFromBundlePage={isFromBundlePage}
            imageConfigWithoutDPR={imageConfigWithoutDPR}
          />
          <ProductDetailImage
            className="outfit-carousal-image"
            imageUrl={plpImagesToDisplay?.[0]?.bigSizeImageUrl}
            zoomImageUrl={plpImagesToDisplay?.[0]?.superSizeImageUrl}
            imageName={currentProduct.name}
            isZoomEnabled={
              isDesktop &&
              isCollectionsZoomEnabled &&
              !getVideoUrl(plpImagesToDisplay?.[0]?.superSizeImageUrl)
            }
            imageConfig={IMG_DATA.imgConfig}
            isOutfitPage={isOutfitPage}
            primaryBrand={primaryBrand}
            fromPLPPage={false}
            onLoadImage={this.onImageLoad(imageFlag)}
            isFromBundlePage={isFromBundlePage}
            onOpenSimpleFullSize={this.handleShowHideFullSizeModalClick}
            isCollectionsZoomEnabled={isCollectionsZoomEnabled}
            imageConfigWithoutDPR={imageConfigWithoutDPR}
          />
          {isCollectionsZoomEnabled && (
            <>
              <div className="enlarged-image-container">
                <div id="portal" className="enlarged-image" />
              </div>
              {isFullSizeModalOpen && !isDesktop && (
                <FullSizeImageModal
                  name={currentProduct.name}
                  selectedPDPImage={this.selectedPDPImage}
                  customOverlayClass="pdp-img-overlay"
                  modalLabel={''}
                  image={plpImagesToDisplay?.[0]?.bigSizeImageUrl}
                  onCloseClick={this.handleShowHideFullSizeModalClick}
                  primaryBrand={primaryBrand}
                  alternateBrand={alternateBrand}
                  images={plpImagesToDisplay}
                  isPinchZoomPDPImageEnabled={isPinchZoomPDPImageEnabled}
                />
              )}
            </>
          )}
        </>
      );
    }
    return this.getMainImageCarousel(imagesToDisplay, imageFlag);
  };

  getMainImageCarousel = (imagesToDisplay, imageFlag) => {
    const {
      currentProduct,
      isOutfitPage,
      alternateBrand,
      isFromBundlePage,
      isCollectionsZoomEnabled,
      isPinchZoomPDPImageEnabled,
    } = this.props;
    const { isFullSizeModalOpen, currentImageIndex, hasMounted } = this.state;
    const primaryBrand = currentProduct?.primaryBrand || alternateBrand;
    const isMobile = getViewportInfo()?.isMobile;
    const isTablet = getViewportInfo()?.isTablet;
    const isDesktop =
      ExecutionEnvironment.canUseDOM && document.body.offsetWidth >= breakpoints.values.lg;
    if (isMobile || isTablet) {
      CAROUSEL_OPTIONS_NEW_DESIGN.arrows = false;
      CAROUSEL_OPTIONS_NEW_DESIGN.dots = true;
    } else {
      CAROUSEL_OPTIONS_NEW_DESIGN.arrows = true;
      CAROUSEL_OPTIONS_NEW_DESIGN.dots = false;
    }

    let filteredImagesToDisplay = imagesToDisplay;

    if (isCollectionsZoomEnabled) {
      CAROUSEL_OPTIONS_NEW_DESIGN.afterChange = (current) =>
        this.setState({ currentImageIndex: current });

      filteredImagesToDisplay = imagesToDisplay?.map((imgData) => {
        return {
          ...imgData,
          regularSizeImageUrl: filterBundleImageUrl(imgData.regularSizeImageUrl),
          bigSizeImageUrl: filterBundleImageUrl(imgData.bigSizeImageUrl),
          iconSizeImageUrl: filterBundleImageUrl(imgData.iconSizeImageUrl),
        };
      });
    }

    const zoomImageUrlSingleImage = filterBundleImageUrl(imagesToDisplay?.[0]?.superSizeImageUrl);

    const isVideoUrlSingleImage = getVideoUrl(zoomImageUrlSingleImage);
    return (
      <>
        {imagesToDisplay && imagesToDisplay.length === 1 ? (
          <ProductDetailImage
            className="outfit-carousal-image"
            imageUrl={filterBundleImageUrl(imagesToDisplay?.[0]?.regularSizeImageUrl)}
            zoomImageUrl={zoomImageUrlSingleImage}
            imageName={currentProduct.name}
            isZoomEnabled={
              (!hasMounted || isDesktop) && isCollectionsZoomEnabled && !isVideoUrlSingleImage
            }
            imageConfig={IMG_DATA.imgConfig}
            isOutfitPage={isOutfitPage}
            primaryBrand={primaryBrand}
            fromPLPPage={false}
            onLoadImage={this.onImageLoad(imageFlag)}
            isFromBundlePage={isFromBundlePage}
            onOpenSimpleFullSize={this.handleShowHideFullSizeModalClick}
            isCollectionsZoomEnabled={isCollectionsZoomEnabled}
          />
        ) : (
          <Carousel
            options={CAROUSEL_OPTIONS_NEW_DESIGN}
            carouselConfig={{
              autoplay: false,
            }}
          >
            {imagesToDisplay &&
              imagesToDisplay.map((image) => {
                const imgFilteredUrl = filterBundleImageUrl(image?.regularSizeImageUrl);
                const zoomImageUrl = filterBundleImageUrl(image?.superSizeImageUrl);
                const isVideoUrl = getVideoUrl(zoomImageUrl);
                return (
                  <ProductDetailImage
                    className="outfit-carousal-image"
                    imageUrl={imgFilteredUrl}
                    imageName={currentProduct.name}
                    isZoomEnabled={isDesktop && isCollectionsZoomEnabled && !isVideoUrl}
                    zoomImageUrl={zoomImageUrl}
                    imageConfig={IMG_DATA.imgConfig}
                    isOutfitPage={isOutfitPage}
                    primaryBrand={primaryBrand}
                    fromPLPPage={false}
                    onLoadImage={this.onImageLoad(imageFlag)}
                    isFromBundlePage={isFromBundlePage}
                    onOpenSimpleFullSize={this.handleShowHideFullSizeModalClick}
                    isCollectionsZoomEnabled={isCollectionsZoomEnabled}
                  />
                );
              })}
          </Carousel>
        )}

        {isCollectionsZoomEnabled && (
          <>
            <div className="enlarged-image-container">
              <div id="portal" className="enlarged-image" />
            </div>
            {isFullSizeModalOpen && !isDesktop && (
              <FullSizeImageModal
                name={currentProduct.name}
                selectedPDPImage={this.selectedPDPImage}
                customOverlayClass="pdp-img-overlay"
                modalLabel={''}
                image={filterBundleImageUrl(imagesToDisplay?.[currentImageIndex]?.bigSizeImageUrl)}
                onCloseClick={this.handleShowHideFullSizeModalClick}
                primaryBrand={primaryBrand}
                alternateBrand={alternateBrand}
                images={filteredImagesToDisplay}
                isPinchZoomPDPImageEnabled={isPinchZoomPDPImageEnabled}
              />
            )}
          </>
        )}
      </>
    );
  };

  getBundleProductsGrid = () => {
    const {
      currentBundle,
      currencyAttributes,
      currencySymbol,
      alternateBrand,
      isHideOriginalPriceAndMarchantTagEnabled,
      isBundleOutfitInfluencerHandleEnabled,
      isInfluencerPicksEnabled,
      isNewSwatchUrlEnabled,
      onQuickViewOpenClick,
      promotionsData,
      isUpdatedPromotionsDisplayEnabled,
      serverTime,
      isBuyMorePromoEnabled,
      isDynamicBadgeEnabled,
      resetSlpLoadingState,
      inYourBagProducts,
      isKeepAliveEnabled,
      productAvailable,
      isLoading,
      retrievedProductInfo,
      isSplittingExtendedSizesEnabled,
      isDynamicBadgeEnabledAbtest,
    } = this.props;
    const bundleProductsLength =
      (retrievedProductInfo &&
        retrievedProductInfo?.bundleProductsCount &&
        retrievedProductInfo.bundleProductsCount?.length) ||
      4;
    const checkCurrentBundle =
      Array.isArray(currentBundle) && currentBundle.length > 0 && currentBundle[0].products;
    return (isLoading && !productAvailable) || !checkCurrentBundle ? (
      <Row>
        <Col colSize={{ small: 6, medium: 8, large: 12 }} className={'plp-grid-skelton'}>
          {[...Array(bundleProductsLength)].map(() => (
            <StyledPaginatedGridSkeleton>
              <LoaderSkelton className="grid-image-skeleton" />
              <div className="grid-price-skeleton">
                <LoaderSkelton width="100px" />
                <LoaderSkelton width="32px" />
              </div>

              <LoaderSkelton className="grid-promo-skeleton" />
              <LoaderSkelton className="grid-title-skeleton" />
            </StyledPaginatedGridSkeleton>
          ))}
        </Col>
      </Row>
    ) : (
      <BundleProductsGrid
        currentBundle={currentBundle}
        currencySymbol={currencySymbol}
        currencyAttributes={currencyAttributes}
        alternateBrand={alternateBrand}
        isHideOriginalPriceAndMarchantTagEnabled={isHideOriginalPriceAndMarchantTagEnabled}
        isBundleOutfitInfluencerHandleEnabled={isBundleOutfitInfluencerHandleEnabled}
        isInfluencerPicksEnabled={isInfluencerPicksEnabled}
        isNewSwatchUrlEnabled={isNewSwatchUrlEnabled}
        onQuickViewOpenClick={onQuickViewOpenClick}
        promotionsData={promotionsData}
        isUpdatedPromotionsDisplayEnabled={isUpdatedPromotionsDisplayEnabled}
        serverTime={serverTime}
        isBuyMorePromoEnabled={isBuyMorePromoEnabled}
        isDynamicBadgeEnabled={isDynamicBadgeEnabled}
        resetSlpLoadingState={resetSlpLoadingState}
        inYourBagProducts={inYourBagProducts}
        isKeepAliveEnabled={isKeepAliveEnabled}
        isSplittingExtendedSizesEnabled={isSplittingExtendedSizesEnabled}
        isDynamicBadgeEnabledAbtest={isDynamicBadgeEnabledAbtest}
      />
    );
  };

  getBundleProductsList = () => {
    const {
      currentBundle,
      handleAddToBag,
      addToFavorites,
      addToBagEcom,
      currentState,
      isLoggedIn,
      addToBagErrorId,
      addToBagError,
      isKeepAliveEnabled,
      currencyAttributes,
      currency,
      AddToFavoriteErrorMsg,
      removeAddToFavoritesErrorMsg,
      isOnModelAbTestBundlePdp,
      isInternationalShipping,
      isEnabledOutfitAddedCTA,
      showAddedToBagCta,
      wishList,
      onSetLastDeletedItemIdAction,
      isOutfitPage,
      errorMessages,
      alternateBrand,
      isHideOriginalPriceAndMarchantTagEnabled,
      isBundleOutfitInfluencerHandleEnabled,
      inYourBagProducts,
      isLoading,
      productAvailable,
      retrievedProductInfo,
      isSplittingExtendedSizesEnabled,
    } = this.props;
    const bundleProductsLength =
      (retrievedProductInfo &&
        retrievedProductInfo?.bundleProductsCount &&
        retrievedProductInfo.bundleProductsCount?.length) ||
      4;
    const checkCurrentBundle =
      Array.isArray(currentBundle) && currentBundle.length > 0 && currentBundle[0].products;
    return (isLoading && !productAvailable) || !checkCurrentBundle ? (
      <BundleProductListLoaderContext.Provider value={bundleProductsLength}>
        <BundleProductListLoadingComponent />
      </BundleProductListLoaderContext.Provider>
    ) : (
      <Row fullBleed className="product-items-section">
        <Col colSize={{ small: 6, medium: 8, large: 12 }}>
          <BundleProductListLoaderContext.Provider value={bundleProductsLength}>
            <BundleProductListView
              currentBundle={currentBundle}
              handleAddToBag={handleAddToBag}
              addToFavorites={addToFavorites}
              addToBagEcom={addToBagEcom}
              currentState={currentState}
              isLoggedIn={isLoggedIn}
              addToBagErrorId={addToBagErrorId}
              addToBagError={addToBagError}
              isKeepAliveEnabled={isKeepAliveEnabled}
              currencySymbol={currency}
              currencyAttributes={currencyAttributes}
              AddToFavoriteErrorMsg={AddToFavoriteErrorMsg}
              removeAddToFavoritesErrorMsg={removeAddToFavoritesErrorMsg}
              isOnModelAbTestBundlePdp={isOnModelAbTestBundlePdp}
              isInternationalShipping={isInternationalShipping}
              showAddedToBagCta={showAddedToBagCta}
              isEnabledOutfitAddedCTA={isEnabledOutfitAddedCTA}
              onSetLastDeletedItemIdAction={onSetLastDeletedItemIdAction}
              wishList={wishList}
              isOutfitPage={isOutfitPage}
              errorMessages={errorMessages}
              alternateBrand={alternateBrand}
              isHideOriginalPriceAndMarchantTagEnabled={isHideOriginalPriceAndMarchantTagEnabled}
              isBundleOutfitInfluencerHandleEnabled={isBundleOutfitInfluencerHandleEnabled}
              inYourBagProducts={inYourBagProducts}
              isSplittingExtendedSizesEnabled={isSplittingExtendedSizesEnabled}
            />
          </BundleProductListLoaderContext.Provider>
        </Col>
      </Row>
    );
  };

  renderCurrentView = (currentView) => {
    const { currentBundle, productAvailable, isLoading } = this.props;
    const currentBundleAvailable =
      Array.isArray(currentBundle) && currentBundle.length > 0 && currentBundle[0].products;
    const addSkeletonClass = (isLoading && !productAvailable) || !currentBundleAvailable;
    return currentView === 'grid' ? (
      <Row
        className={`bundle-product-grid-container ${
          addSkeletonClass ? 'bundle-product-grid-skeleton' : ''
        }`}
      >
        {this.getBundleProductsGrid()}
      </Row>
    ) : (
      <Row className="bundle-product-list-container">{this.getBundleProductsList()}</Row>
    );
  };

  setCurrentView = (selectedOption) => {
    this.setState({ currentView: selectedOption });
  };

  setBuildYourExperienceState = (value) => {
    this.setState({
      displayBuildYourCollectionExperience: value,
    });
  };

  renderBuildYourExperience = ({ fullWidth }) => {
    const { isCollectionsZoomEnabled } = this.props;
    return (
      <BuildYourExperienceEntryBlock
        className="build-your-experience-wrapper"
        setBuildYourExperienceState={this.setBuildYourExperienceState}
        fullWidth={fullWidth}
        isCollectionsZoomEnabled={isCollectionsZoomEnabled}
      />
    );
  };

  renderBuildYourExperienceDepartmentView = () => {
    const {
      currencySymbol,
      departmentBundleProducts,
      isKeepAliveEnabled,
      currencyAttributes,
      alternateBrand,
      isHideOriginalPriceAndMarchantTagEnabled,
      isDynamicBadgeEnabled,
      handlePJPlannerMultiItemAddToBag,
      addToCollectionList,
      currentProduct,
      resetDesignYourBagDataAction,
      removeProductFromCollectionAction,
      increaseQuantityInCollectionsAction,
      decreaseQuantityInCollectionsAction,
      atbUnavailableProducts,
      multipleAddToBagError,
      addToBagError,
      clearAddToBagErrorOnUpdate,
      isPJPlannerStickyMiniBagEnabled,
    } = this.props;
    const departmentList = this.getDepartmentList();
    const primaryBrand = currentProduct?.primaryBrand || alternateBrand;
    return (
      <BuildYourExperienceDepartmentView
        className="build-your-experience-department-view-wrapper main-bundle-description-section"
        setBuildYourExperienceState={this.setBuildYourExperienceState}
        currencySymbol={currencySymbol}
        departmentList={departmentList}
        departmentBundleProducts={departmentBundleProducts}
        isKeepAliveEnabled={isKeepAliveEnabled}
        currencyAttributes={currencyAttributes}
        alternateBrand={alternateBrand}
        isHideOriginalPriceAndMarchantTagEnabled={isHideOriginalPriceAndMarchantTagEnabled}
        isDynamicBadgeEnabled={isDynamicBadgeEnabled}
        addToCollectionList={addToCollectionList}
        handlePJPlannerMultiItemAddToBag={handlePJPlannerMultiItemAddToBag}
        primaryBrand={primaryBrand}
        resetDesignYourBagDataAction={resetDesignYourBagDataAction}
        removeProductFromCollectionAction={removeProductFromCollectionAction}
        increaseQuantityInCollectionsAction={increaseQuantityInCollectionsAction}
        decreaseQuantityInCollectionsAction={decreaseQuantityInCollectionsAction}
        atbUnavailableProducts={atbUnavailableProducts}
        multipleAddToBagError={multipleAddToBagError}
        addToBagError={addToBagError}
        clearAddToBagErrorOnUpdate={clearAddToBagErrorOnUpdate}
        isPJPlannerStickyMiniBagEnabled={isPJPlannerStickyMiniBagEnabled}
      />
    );
  };

  getDepartmentList = () => {
    const { departmentBundleProducts } = this.props;

    if (!departmentBundleProducts || Object.keys(departmentBundleProducts)?.length === 0) {
      return [];
    }
    return Object.keys(departmentBundleProducts);
  };

  handleShowHideFullSizeModalClick(e) {
    e?.preventDefault();
    requestAnimationFrame(() => {
      const { isFullSizeModalOpen } = this.state;
      const isDesktop =
        ExecutionEnvironment.canUseDOM && document.body.offsetWidth >= breakpoints.values.lg;
      this.selectedPDPImage = !isDesktop ? e?.target?.currentSrc : '';
      this.setState({ isFullSizeModalOpen: !isFullSizeModalOpen });
    });
  }

  renderSeoSection = (currentProduct) => {
    const seoProps = getSeoProps(currentProduct);
    if (seoProps) {
      const { title, description } = seoProps;
      return <NextSeo title={title} description={description} />;
    }
    return null;
  };

  renderBackToCollections = () => {
    const backButtonLabel = renderLabel(
      this.dynamicBrowseLabels.lbl_build_your_experience_back_button,
      'lbl_build_your_experience_back_button'
    );
    return (
      <Row className="back-to-collections-container">
        <button
          onClick={() => {
            setTimeout(() => {
              this.setBuildYourExperienceState(false);
            }, 0);
          }}
          className="back-to-collections-button"
        >
          <Image
            src={getIconPath('medium-left-arrow')}
            alt=""
            className="back-to-collections-left-arrow"
          />
          <BodyCopy className="back-to-collections-label" fontFamily="secondary" fontSize="fs16">
            {backButtonLabel}
          </BodyCopy>
        </button>
      </Row>
    );
  };

  render() {
    const {
      className,
      currentProduct,
      topPromos,
      fromPLPPage,
      retrievedProductInfo,
      isShowPriceRangeKillSwitch,
      showPriceRangeForABTest,
      isHideOriginalPrice,
      currencyAttributes,
      currency,
      primaryBrand,
      isNewDesignToggleOptionsEnabled,
      isCombinedKitsEnabled,
      carryForwardImgUrlValue,
      kitGrouping,
      toastModalState,
      showToastModalAction,
      isBundlePageRedesign,
      errorMessages,
      isOutfitPage,
      isPJPlannerExperienceEnabled,
      isCollectionsZoomEnabled,
      departmentBundleProducts,
    } = this.props;
    const { currentView, hasMounted, displayBuildYourCollectionExperience } = this.state;
    const departmentList = this.getDepartmentList();

    const hasCombinedKits = this.getHasCombinedKits({ isCombinedKitsEnabled, kitGrouping });

    if (currentProduct && JSON.stringify(currentProduct) !== '{}') {
      const { colorFitsSizesMap, generalProductId } = currentProduct;
      const currentColorEntry = getMapSliceForColorProductId(colorFitsSizesMap, generalProductId);
      return (
        <>
          {this.renderSeoSection(currentProduct)}
          <div className={className}>
            {!displayBuildYourCollectionExperience
              ? this.getBreadCrumb()
              : this.renderBackToCollections()}
            <Row
              className={`product-container${getBuildYourExperienceClass(
                displayBuildYourCollectionExperience
              )}`}
              isBundlePageRedesign
              id="productDetailsSection"
            >
              {!displayBuildYourCollectionExperience && (
                <Col colSize={{ small: 6, medium: 8, large: 12 }}>
                  {topPromos && topPromos.length > 0 && <PromoPDPBanners promos={topPromos} />}
                </Col>
              )}
              <Col
                colSize={{ small: 6, medium: 3, large: 4 }}
                className={`main-bundle-image${getBuildYourExperienceImageClass(
                  displayBuildYourCollectionExperience
                )}${getCollectionZoomClass(isCollectionsZoomEnabled)}`}
              >
                {this.renderPlpPdpMainImage()}
              </Col>
              {!displayBuildYourCollectionExperience && (
                <>
                  <Col
                    colSize={{ small: 6, medium: 4, large: 7 }}
                    className="main-bundle-description main-bundle-description-section"
                  >
                    <Row fullBleed className="product-summary-section">
                      <Col
                        className="product-detail-section"
                        colSize={{ small: 6, medium: 8, large: 12 }}
                        ref={this.mainBundleDescriptionRef}
                      >
                        <div className={`hide-on-desktop hide-on-tablet`}>
                          {this.combinedKits(true)}
                        </div>
                        <div className={`${className}`}>
                          {this.getProductSummary(currentColorEntry)}
                        </div>

                        {this.getProductDescription(currentColorEntry)}
                      </Col>
                    </Row>
                    <div
                      className={`hide-on-mobile combined-kit-container${getRemoveMarginTopClass(
                        hasCombinedKits
                      )}`}
                    >
                      {this.combinedKits(false)}
                    </div>
                    {shouldDisplayFullWidthPJPlannerEntryBlock({
                      isPJPlannerExperienceEnabled,
                      displayBuildYourCollectionExperience,
                      hasCombinedKits,
                      departmentList,
                    }) && this.renderBuildYourExperience({ fullWidth: true })}
                  </Col>
                  <Col
                    className="product-build-your-collection-col"
                    colSize={{ small: 6, medium: 8, large: 12 }}
                  >
                    {shouldDisplayPJPlannerEntryBlock({
                      isPJPlannerExperienceEnabled,
                      displayBuildYourCollectionExperience,
                      hasCombinedKits,
                      departmentList,
                    }) && this.renderBuildYourExperience({ fullWidth: false })}
                  </Col>
                </>
              )}
              {displayBuildYourCollectionExperience &&
                !isEmpty(departmentBundleProducts) &&
                this.renderBuildYourExperienceDepartmentView()}
            </Row>

            {!displayBuildYourCollectionExperience && (
              <>
                {!hasMounted || isNewDesignToggleOptionsEnabled ? (
                  <StyledToggleOptionWrapper>
                    <Switch
                      className="switch"
                      selectedOption={currentView}
                      setSelectedOption={this.setCurrentView}
                      options={TOGGLE_OPTION_CONFIG}
                      storageKey={NEW_DESIGN_STORAGE_KEY}
                    />
                  </StyledToggleOptionWrapper>
                ) : null}
                {this.renderCurrentView(currentView)}
                {this.getRecommendations()}
              </>
            )}
            {toastModalState?.showToast && (
              <ToastModal
                className={className}
                duration={toastModalState?.duration}
                showToastModalAction={showToastModalAction}
              />
            )}
          </div>
        </>
      );
    }
    return (
      <BundleProductSkeletonNewDesign
        breadcrumb={this.getBreadCrumb}
        isBundlePageRedesign={isBundlePageRedesign}
        currentProduct={currentProduct}
        fromPLPPage={fromPLPPage}
        retrievedProductInfo={retrievedProductInfo}
        primaryBrand={primaryBrand}
        currency={currency}
        currencyAttributes={currencyAttributes}
        isShowPriceRangeKillSwitch={isShowPriceRangeKillSwitch}
        showPriceRangeForABTest={showPriceRangeForABTest}
        isHideOriginalPrice={isHideOriginalPrice}
        isCombinedKitsEnabled={isCombinedKitsEnabled}
        carryForwardImgUrlValue={carryForwardImgUrlValue}
        kitGrouping={kitGrouping}
        currentView={currentView}
        errorMessages={errorMessages}
        isOutfitPage={isOutfitPage}
      />
    );
  }
}

BundleProductNewDesign.propTypes = {
  className: PropTypes.string,
  shortDescription: PropTypes.string,
  itemPartNumber: PropTypes.string,
  longDescription: PropTypes.string,
  breadCrumbs: PropTypes.shape({}),
  currentProduct: PropTypes.shape({}),
  currencyAttributes: PropTypes.shape({}).isRequired,
  topPromos: PropTypes.shape({}),
  isOnModelAbTestBundlePdp: PropTypes.bool,
  isOutfitPage: PropTypes.bool,
  errorMessages: PropTypes.shape({}),
  trackAnalyticsClick: PropTypes.func,
  isBundlePageRedesign: PropTypes.bool,
  isKeepAliveEnabled: PropTypes.bool,
  isCombinedKitsEnabled: PropTypes.bool,
  carryForwardImgUrlActn: PropTypes.func,
  carryForwardImgUrlValue: PropTypes.string,
  kitGrouping: PropTypes.shape([]),
  toastModalState: PropTypes.shape({
    duration: PropTypes.number,
    showToast: PropTypes.bool,
  }),
  showToastModalAction: PropTypes.func,
  isPJPlannerExperienceEnabled: PropTypes.bool,
  departmentBundleProducts: PropTypes.shape({}),
};

BundleProductNewDesign.defaultProps = {
  className: '',
  longDescription: '',
  shortDescription: '',
  itemPartNumber: '',
  breadCrumbs: [],
  currentProduct: {},
  topPromos: null,

  isOnModelAbTestBundlePdp: false,
  isOutfitPage: false,
  errorMessages: {},
  trackAnalyticsClick: () => {},
  isBundlePageRedesign: false,
  isKeepAliveEnabled: false,
  isCombinedKitsEnabled: false,
  carryForwardImgUrlActn: () => {},
  carryForwardImgUrlValue: '',
  kitGrouping: [],
  toastModalState: {
    duration: 0,
    showToast: false,
  },
  showToastModalAction: () => {},
  isPJPlannerExperienceEnabled: false,
  departmentBundleProducts: {},
};

export default withStyles(BundleProductNewDesign, styles);
